import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { api } from '../api';
import { ColumnChart, Delta, HBars, fmtNum } from '../charts';
import { Empty, ErrorBox, Loading, fmtDate } from '../ui';

export interface Analytics {
  days: number;
  timezone: string;
  daily: { day: string; newUsers: number; submitted: number; approved: number; rejected: number; chats: number; messages: number; reports: number; stars: number }[];
  totals: {
    newUsers: number; newUsersPrev: number; activeUsers: number; submitted: number; submittedPrev: number;
    messages: number; messagesPrev: number; stars: number; starsPrev: number; activeListings: number; pending: number;
  };
  moderation: { decisions: number; medianMinutes: number | null; p90Minutes: number | null };
  categories: { id: string; name: string; active: number; newInPeriod: number }[];
  searches: { top: SearchRow[]; zeroResults: SearchRow[] };
}
interface SearchRow { query: string; searches: number; zeroResults: number; lastAt: string }

export const fmtMinutes = (m: number | null) => (m === null ? '—' : m < 60 ? `${Math.round(m)} мин` : m < 48 * 60 ? `${(m / 60).toFixed(1).replace('.', ',')} ч` : `${Math.round(m / 1440)} дн.`);

export function AnalyticsPage() {
  const [days, setDays] = useState(30);
  const { data, isLoading, error, refetch } = useQuery({ queryKey: ['analytics', days], queryFn: () => api<Analytics>(`/admin/analytics?days=${days}`) });
  return (
    <div>
      <div className="page-head">
        <div>
          <h1>Аналитика</h1>
          <p className="muted">По дням, время Ташкента. Сравнение — с предыдущим периодом такой же длины.</p>
        </div>
        <div className="segmented" role="group" aria-label="Период">
          {[7, 30, 90].map((d) => (
            <button key={d} type="button" aria-pressed={days === d} onClick={() => setDays(d)}>
              {d} дней
            </button>
          ))}
        </div>
      </div>
      {isLoading ? <Loading /> : error || !data ? <ErrorBox error={error} onRetry={() => void refetch()} /> : <AnalyticsBody a={data} />}
    </div>
  );
}

function AnalyticsBody({ a }: { a: Analytics }) {
  const t = a.totals;
  const decided = a.daily.reduce((s, d) => s + d.approved + d.rejected, 0);
  const rejectedShare = decided ? Math.round((a.daily.reduce((s, d) => s + d.rejected, 0) / decided) * 100) : 0;
  return (
    <>
      <div className="tiles">
        <div className="tile">
          <span className="tile-label">Новые пользователи</span>
          <span className="tile-value">{fmtNum(t.newUsers)}</span>
          <Delta cur={t.newUsers} prev={t.newUsersPrev} />
        </div>
        <div className="tile">
          <span className="tile-label">Заходили в приложение</span>
          <span className="tile-value">{fmtNum(t.activeUsers)}</span>
          <span className="tile-delta">за {a.days} дней</span>
        </div>
        <div className="tile">
          <span className="tile-label">Подано объявлений</span>
          <span className="tile-value">{fmtNum(t.submitted)}</span>
          <Delta cur={t.submitted} prev={t.submittedPrev} />
        </div>
        <div className="tile">
          <span className="tile-label">Сообщений в чатах</span>
          <span className="tile-value">{fmtNum(t.messages)}</span>
          <Delta cur={t.messages} prev={t.messagesPrev} />
        </div>
        <div className="tile">
          <span className="tile-label">Звёзд получено</span>
          <span className="tile-value">{fmtNum(t.stars)} ⭐</span>
          <Delta cur={t.stars} prev={t.starsPrev} />
        </div>
        <div className="tile">
          <span className="tile-label">Время модерации (медиана)</span>
          <span className="tile-value">{fmtMinutes(a.moderation.medianMinutes)}</span>
          <span className="tile-delta">90 % решений — до {fmtMinutes(a.moderation.p90Minutes)}, решений {fmtNum(a.moderation.decisions)}</span>
        </div>
      </div>

      <div className="panels">
        <section className="panel" aria-label="Новые пользователи по дням">
          <div className="chart-head">
            <div>
              <h2>Новые пользователи</h2>
              <p className="sub">Первый вход в Mini App</p>
            </div>
            <span className="chart-total">{fmtNum(t.newUsers)}</span>
          </div>
          <ColumnChart rows={a.daily} series={[{ key: 'newUsers', label: 'Новые' }]} ariaLabel="Новые пользователи по дням" />
        </section>
        <section className="panel" aria-label="Решения модераторов по дням">
          <div className="chart-head">
            <div>
              <h2>Модерация</h2>
              <p className="sub">Решения по дням; отклонено {rejectedShare} %</p>
            </div>
            <span className="chart-total">{fmtNum(decided)}</span>
          </div>
          <ColumnChart
            rows={a.daily}
            series={[{ key: 'approved', label: 'Одобрено' }, { key: 'rejected', label: 'Отклонено и снято' }]}
            ariaLabel="Решения модераторов по дням"
          />
        </section>
        <section className="panel" aria-label="Сообщения в чатах по дням">
          <div className="chart-head">
            <div>
              <h2>Сообщения в чатах</h2>
              <p className="sub">Переписка покупателей и продавцов; новых чатов {fmtNum(a.daily.reduce((s, d) => s + d.chats, 0))}</p>
            </div>
            <span className="chart-total">{fmtNum(t.messages)}</span>
          </div>
          <ColumnChart rows={a.daily} series={[{ key: 'messages', label: 'Сообщения' }]} ariaLabel="Сообщения по дням" />
        </section>
        <section className="panel" aria-label="Подано объявлений по дням">
          <div className="chart-head">
            <div>
              <h2>Подано объявлений</h2>
              <p className="sub">Новые и повторно отправленные на проверку</p>
            </div>
            <span className="chart-total">{fmtNum(t.submitted)}</span>
          </div>
          <ColumnChart rows={a.daily} series={[{ key: 'submitted', label: 'Подано' }]} ariaLabel="Подано объявлений по дням" />
        </section>
        <section className="panel" aria-label="Звёзды по дням">
          <div className="chart-head">
            <div>
              <h2>Продвижение за звёзды</h2>
              <p className="sub">Оплачено и не возвращено</p>
            </div>
            <span className="chart-total">{fmtNum(t.stars)} ⭐</span>
          </div>
          {t.stars > 0 ? (
            <ColumnChart rows={a.daily} series={[{ key: 'stars', label: 'Звёзды' }]} ariaLabel="Звёзды по дням" />
          ) : (
            <Empty text="Оплат за этот период не было." />
          )}
        </section>
        <section className="panel" aria-label="Категории">
          <h2>Категории</h2>
          <p className="sub">Активные объявления сейчас; в подсказке — новые за период</p>
          {a.categories.length ? (
            <HBars items={a.categories.map((c) => ({ label: c.name, value: c.active, hint: `новых за ${a.days} дн.: ${c.newInPeriod}` }))} />
          ) : (
            <Empty text="Объявлений пока нет." />
          )}
        </section>
        <section className="panel" aria-label="Популярные запросы">
          <h2>Что ищут</h2>
          <p className="sub">Число поисков за всё время; показаны запросы, которые искали в этом периоде</p>
          <SearchTable rows={a.searches.top} empty="Поисковых запросов пока нет." />
        </section>
        <section className="panel" aria-label="Запросы без результатов">
          <h2>Ищут, но не находят</h2>
          <p className="sub">Запросы без результатов — подсказка, каких объявлений не хватает или какие синонимы добавить (раздел «Поиск»)</p>
          <SearchTable rows={a.searches.zeroResults} empty="Все запросы что-то находят." zero />
        </section>
      </div>
    </>
  );
}

function SearchTable({ rows, empty, zero }: { rows: SearchRow[]; empty: string; zero?: boolean }) {
  if (!rows.length) return <Empty text={empty} />;
  return (
    <table className="mini-table">
      <thead>
        <tr>
          <th>Запрос</th>
          <th className="num-col">{zero ? 'Без результатов' : 'Поисков'}</th>
          <th>Последний</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => (
          <tr key={r.query}>
            <td>{r.query}</td>
            <td className="num-col">{fmtNum(zero ? r.zeroResults : r.searches)}</td>
            <td className="muted">{fmtDate(r.lastAt)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
